import { atom, getDefaultStore } from "jotai";

export const IMPERSONATION_STATE_KEY = "impersonation_state";

export type ImpersonationTargetType = "DOCTOR" | "MEDICAL_ASSISTANT";

export interface ImpersonationState {
  isActive: boolean;
  sessionId: string | null;
  adminCognitoSub: string | null;
  targetUsername: string | null;
  targetType: ImpersonationTargetType | null;
  physicianId: string | null;
  medicalAssistantId: string | null;
  reason: string | null;
  startedAt: string | null;
  expiresAt: string | null;
}

const defaultState: ImpersonationState = {
  isActive: false,
  sessionId: null,
  adminCognitoSub: null,
  targetUsername: null,
  targetType: null,
  physicianId: null,
  medicalAssistantId: null,
  reason: null,
  startedAt: null,
  expiresAt: null,
};

function getInitialState(): ImpersonationState {
  const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
  if (!raw) return defaultState;
  try {
    const stored = JSON.parse(raw) as ImpersonationState;
    // Leave the key in place: expireLapsedImpersonation() (run at boot) uses it
    // to evict the target's leftover tokens, then clears it.
    if (stored.expiresAt && new Date(stored.expiresAt) < new Date()) {
      return defaultState;
    }
    return stored;
  } catch {
    return defaultState;
  }
}

const baseImpersonationAtom = atom<ImpersonationState>(getInitialState());

export const impersonationAtom = atom(
  (get) => get(baseImpersonationAtom),
  (_get, set, newState: ImpersonationState) => {
    set(baseImpersonationAtom, newState);
    if (newState.isActive) {
      localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(newState));
    } else {
      localStorage.removeItem(IMPERSONATION_STATE_KEY);
    }
  },
);

export const defaultImpersonationState = defaultState;

// Sync atom across tabs: when another tab clears or updates impersonation state
// in localStorage, update this tab's atom so the banner disappears immediately.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== IMPERSONATION_STATE_KEY) return;
    const store = getDefaultStore();
    if (e.newValue === null) {
      store.set(baseImpersonationAtom, defaultState);
    } else {
      try {
        store.set(
          baseImpersonationAtom,
          JSON.parse(e.newValue) as ImpersonationState,
        );
      } catch {
        // Ignore malformed data
      }
    }
  });
}
